import * as React from 'react';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Auth from '../../utils/auth';
import ReCAPTCHA from 'react-google-recaptcha';
import Button from '../ui/Button';

const SITE_KEY = '6LdhiQUrAAAAAPtJtmz4BW8sc-9aFDvTDNem1DG0';

export default function LogInComponent({ admin = false }) {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [captchaToken, setCaptchaToken] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = (location.state?.from || '').startsWith('/zapier-Consent')
        ? location.state.from
        : '/';
    const captchaRef = React.useRef();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };
    const handleCaptcha = (token) => {
        setCaptchaToken(token);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // CAPTCHA check only for normal users
        if (!admin && failedAttempts >= 2 && !captchaToken) {
            setError('Please complete the CAPTCHA.');
            return;
        }

        setLoading(true);

        // pick the right login method
        const loginFn = admin
            ? () => Auth.adminLogin(formData.email, formData.password)
            : () =>
                Auth.login(
                    formData.email,
                    formData.password,
                    failedAttempts >= 2 ? captchaToken : null
                );

        const { success, message } = await loginFn();

        if (success) {
            setFailedAttempts(0);
            navigate(returnTo);
        } else {
            setFailedAttempts((prev) => prev + 1);
            setError(message);
            captchaRef.current?.reset();
            setCaptchaToken('');
        }

        setLoading(false);
    };

    return (
        <main className="w-full">
            <div className="mx-auto my-8 flex w-full max-w-md flex-col gap-4 rounded-xl bg-white px-6 py-7 shadow-card">
                <div>
                    <h1 className="mb-2.5 text-2xl text-ink">Welcome!</h1>
                    <p className="text-base text-gray-550">Sign in to continue.</p>
                </div>

                {error && (
                    <div className="rounded-md bg-error/10 px-4 py-3 text-sm text-error">{error}</div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-5">
                        <label htmlFor="email" className="cb-label">Email</label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="example@email.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="cb-input"
                        />
                    </div>
                    <div className="mb-5">
                        <label htmlFor="password" className="cb-label">Password</label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            placeholder="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className="cb-input"
                        />
                    </div>
                    {failedAttempts >= 2 && (
                        <div className="mb-5">
                            <ReCAPTCHA
                                ref={captchaRef}
                                sitekey={SITE_KEY}
                                onChange={handleCaptcha}
                            />
                        </div>
                    )}

                    <Button type="submit" disabled={loading} className="mb-5 w-full">
                        {loading ? (
                            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        ) : (
                            'Log in'
                        )}
                    </Button>
                </form>

                <p className="self-center text-sm text-gray-550">
                    Don&apos;t have an account?{' '}
                    <a href="https://go.clientbloom.ai/" className="font-medium text-purple hover:underline">
                        Sign up
                    </a>
                </p>
            </div>
        </main>
    );
}
